import { GQWasteEmissionsMethodology } from '@watershed/shared-universal/generated/graphql-schema-types';
import { CanonicalDatasetKind } from '@watershed/constants/datasets';
import { getKindIsFacilitiesDataset } from './measurement/BuildingUtils';
import isIncludedIn from './utils/isIncludedIn';
import { PrimaryTypeName } from './batSchemas/types';

export const CANONICAL_DATASET_BEA_CODE_MAPPING = {
  CloudUsage: ['naics_518200'],
  CloudUsageAndCost: ['naics_518200'],
  CryptoTransactions: ['naics_518200'],
  EnergyAttributeCertificatesPurchased: ['naics_221100'],
  Flights: ['naics_481000'],
  PrivateFlights: ['naics_481000'],
  Hotels: ['naics_721000'],
  Rails: ['naics_482000'],
  Refrigerants: ['naics_333415'],
  Waste: ['naics_562000'],
  DataCenters: ['naics_221100', 'naics_518200'],
  Food: ['naics_722110', 'naics_722211', 'naics_722a00'],
  Vehicles: [
    'naics_324110',
    'naics_447000',
    'naics_485000',
    'naics_532100',
    'naics_221100',
  ],
  WaterUsage: ['naics_221300', 'naics_312110'],
  UpstreamLogistics: [
    'naics_481000',
    'naics_482000',
    'naics_483000',
    'naics_484000',
    'naics_491000',
    'naics_492000',
  ],
  Employees: [
    'naics_562000',
    'naics_324110',
    'naics_447000',
    'naics_485000',
    'naics_532100',
  ],
  Packaging: [
    'naics_326130',
    'naics_326140',
    'naics_326150',
    'naics_326160',
    'naics_326190',
    'naics_326220',
    'naics_326290',
    'naics_327100',
    'naics_327200',
    'naics_331200',
    'naics_33131b',
    'naics_331420',
    'naics_33211b',
  ],
} satisfies Partial<{
  [key in CanonicalDatasetKind]: Array<string>;
}>;

export type BeaCodeMappedCanonicalDatasetKind =
  keyof typeof CANONICAL_DATASET_BEA_CODE_MAPPING;

export function isBeaCodeMappedCanonicalDatasetKind(
  kind: CanonicalDatasetKind
): kind is BeaCodeMappedCanonicalDatasetKind {
  return kind in CANONICAL_DATASET_BEA_CODE_MAPPING;
}

/**
 * We're defining some groups for these datasets as part of the milestones
 * project as we prototype. This may eventually want to graduate to a model if
 * we get traction here.
 */
export enum CanonicalDatasetGroup {
  Facilities = 'Facilities',
  BusinessData = 'BusinessData',
  CarbonCreditRetirements = 'CarbonCreditRetirements',
  GhgRemoval = 'GhgRemoval',
  Cloud = 'Cloud',
  Custom = 'Custom',
  DataCenters = 'DataCenters',
  Employees = 'Employees',
  Financials = 'Financials',
  Logistics = 'Logistics',
  Products = 'Products',
  Refrigerants = 'Refrigerants',
  Sites = 'Sites',
  Substances = 'Substances',
  Travel = 'Travel',
  Waste = 'Waste',
  Water = 'Water',
  FinancedEmissions = 'FinancedEmissions',
  Utilities = 'Utilities',
  Buildings = 'Buildings',
  PurchaseOrders = 'PurchaseOrders',
}

export type CanonicalDatasetGroupOrAcrossDatasets =
  | CanonicalDatasetGroup
  | 'AcrossDatasets';

/**
 * These are the canonical datasets kinds that are allowed for Essential customers
 * and Standard or above.
 */

export const CANONICAL_DATASETS_FOR_ESSENTIAL = [
  CanonicalDatasetKind.Buildings,
  CanonicalDatasetKind.Utilities,
  CanonicalDatasetKind.Flights,
  CanonicalDatasetKind.Hotels,
  CanonicalDatasetKind.Employees,
  CanonicalDatasetKind.Revenue,
  CanonicalDatasetKind.PrivateFlights,
  CanonicalDatasetKind.EmployeeTravelExpenses,
  CanonicalDatasetKind.DirectEmissions,
];

export const CANONICAL_DATASETS_FOR_STANDARD = [
  ...CANONICAL_DATASETS_FOR_ESSENTIAL,
  CanonicalDatasetKind.OperatingExpenses,
  CanonicalDatasetKind.CapitalExpenses,
  CanonicalDatasetKind.CostOfRevenue,
  CanonicalDatasetKind.CloudCosts,
  CanonicalDatasetKind.CloudUsage,
  CanonicalDatasetKind.CloudUsageAndCost,
  CanonicalDatasetKind.DataCenters,
  CanonicalDatasetKind.Rails,
  CanonicalDatasetKind.Vehicles,
  CanonicalDatasetKind.UpstreamLogistics,
  CanonicalDatasetKind.DownstreamLogistics,
  // CG/Apparel datasets
  CanonicalDatasetKind.EndOfLifeTreatmentOfSoldProducts,
  CanonicalDatasetKind.Packaging,
  CanonicalDatasetKind.MaterialAggregate, // this is Products & Materials TODO(tammy): Rename this CanonicalDatasetKind
  CanonicalDatasetKind.SupplierFactoriesTier1,
  CanonicalDatasetKind.SupplierUpstreamLogistics,
  CanonicalDatasetKind.UseOfSoldProductsFuels, // this is Use of Sold Products TODO(tammy): Rename this CanonicalDatasetKind
];

export const CANONICAL_DATASETS_FOR_ENTERPRISE = [
  ...CANONICAL_DATASETS_FOR_STANDARD,
  CanonicalDatasetKind.CorporateInvestments,
];

export const CANONICAL_DATASETS_FOR_CSRD_E1 = [
  CanonicalDatasetKind.Buildings,
  CanonicalDatasetKind.Vehicles,
  CanonicalDatasetKind.PrivateFlights,
  CanonicalDatasetKind.Revenue,
  CanonicalDatasetKind.EnergyAttributeCertificatesPurchased,
  CanonicalDatasetKind.CarbonCreditRetirements,
  CanonicalDatasetKind.GhgRemoval,
];
export const CANONICAL_DATASETS_FOR_CSRD_E2 = [
  CanonicalDatasetKind.Substances, // for measured substance outflow
  CanonicalDatasetKind.Vehicles,
  CanonicalDatasetKind.Microplastics,
  CanonicalDatasetKind.MaterialAggregate, // Products and Materials
];
export const CANONICAL_DATASETS_FOR_CSRD_E3 = [
  CanonicalDatasetKind.Buildings,
  CanonicalDatasetKind.Utilities,
  CanonicalDatasetKind.WaterUsage,
];
export const CANONICAL_DATASETS_FOR_CSRD_E4 = [CanonicalDatasetKind.Sites];
export const CANONICAL_DATASETS_FOR_CSRD_E5 = [
  CanonicalDatasetKind.MaterialAggregate, // Products and Materials
  CanonicalDatasetKind.SoldProductCircularity,
  CanonicalDatasetKind.Buildings,
  CanonicalDatasetKind.Waste,
];
export const CANONICAL_DATASETS_FOR_CSRD_SOCIAL_AND_GOVERNANCE = [
  CanonicalDatasetKind.WorkforceSocial,
  CanonicalDatasetKind.SafetyIncidents, // Health and safety incidents
  CanonicalDatasetKind.WorkplaceReports,
  CanonicalDatasetKind.HumanRightsViolations,
  CanonicalDatasetKind.BriberyAndCorruption,
  CanonicalDatasetKind.Lobbying,
  CanonicalDatasetKind.OperationalPayments,
];

/**
 * Set some groupings for the canonical dataset kinds. Typescript will force
 * this to be exhaustive on CanonicalDatasetKind.
 */
export const CANONICAL_DATASET_GROUPING: {
  [key in CanonicalDatasetKind]: CanonicalDatasetGroup;
} = {
  BillOfMaterials: CanonicalDatasetGroup.Products,
  Buildings: CanonicalDatasetGroup.Facilities,
  CapitalExpenses: CanonicalDatasetGroup.Financials,
  CarbonCreditRetirements: CanonicalDatasetGroup.CarbonCreditRetirements,
  CareInstructions: CanonicalDatasetGroup.Products,
  ChartOfAccounts: CanonicalDatasetGroup.Financials,
  CloudCosts: CanonicalDatasetGroup.Cloud,
  CloudUsage: CanonicalDatasetGroup.Cloud,
  CloudUsageAndCost: CanonicalDatasetGroup.Cloud,
  CorporateInvestments: CanonicalDatasetGroup.Financials,
  CostOfRevenue: CanonicalDatasetGroup.Financials,
  CryptoHoldings: CanonicalDatasetGroup.Financials,
  CryptoTransactions: CanonicalDatasetGroup.Financials,
  CustomEmissionsFactor: CanonicalDatasetGroup.Custom,
  DataCenters: CanonicalDatasetGroup.DataCenters,
  DirectEmissions: CanonicalDatasetGroup.BusinessData,
  DownstreamLogistics: CanonicalDatasetGroup.Logistics,
  EmployeeTravelExpenses: CanonicalDatasetGroup.Travel,
  Employees: CanonicalDatasetGroup.Employees,
  EndOfLifeTreatmentOfSoldProducts: CanonicalDatasetGroup.Products,
  EnergyAttributeCertificatesPurchased: CanonicalDatasetGroup.Facilities,
  Factories: CanonicalDatasetGroup.Products,
  Financials: CanonicalDatasetGroup.Financials,
  Flights: CanonicalDatasetGroup.Travel,
  Food: CanonicalDatasetGroup.Products,
  GhgRemoval: CanonicalDatasetGroup.GhgRemoval,
  HistoricalResults: CanonicalDatasetGroup.BusinessData,
  Hotels: CanonicalDatasetGroup.Travel,
  MaterialAggregate: CanonicalDatasetGroup.Products,
  MaterialContents: CanonicalDatasetGroup.Products,
  PrivateFlights: CanonicalDatasetGroup.Travel,
  OperatingExpenses: CanonicalDatasetGroup.Financials,
  Packaging: CanonicalDatasetGroup.Products,
  ProductsPurchased: CanonicalDatasetGroup.Products,
  ProductsSold: CanonicalDatasetGroup.Products,
  Refrigerants: CanonicalDatasetGroup.Refrigerants,
  Rails: CanonicalDatasetGroup.Travel,
  Revenue: CanonicalDatasetGroup.Financials,
  SoldProductCircularity: CanonicalDatasetGroup.Products,
  Sites: CanonicalDatasetGroup.Sites,
  SupplierFactoriesTier1: CanonicalDatasetGroup.Products,
  SupplierUpstreamLogistics: CanonicalDatasetGroup.Products,
  TextileProductSales: CanonicalDatasetGroup.Products,
  UpstreamLogistics: CanonicalDatasetGroup.Logistics,
  UseOfGoods: CanonicalDatasetGroup.Products,
  UseOfSoldProductsDataTransfer: CanonicalDatasetGroup.Products,
  UseOfSoldProductsRefrigerants: CanonicalDatasetGroup.Products,
  UseOfSoldProductsFuels: CanonicalDatasetGroup.Products,
  UseOfSoldProductsVehicles: CanonicalDatasetGroup.Products,
  UseOfSoldProductsCommonRetail: CanonicalDatasetGroup.Products,
  UsePhaseOfGoods: CanonicalDatasetGroup.Products,
  Utilities: CanonicalDatasetGroup.Facilities,
  Substances: CanonicalDatasetGroup.Substances,
  SubstanceInflow: CanonicalDatasetGroup.Substances,
  Vehicles: CanonicalDatasetGroup.Travel,
  WeightOfGoodsPurchased: CanonicalDatasetGroup.Products,
  Waste: CanonicalDatasetGroup.Waste,
  WaterUsage: CanonicalDatasetGroup.Water,
  FinancedEmissions: CanonicalDatasetGroup.FinancedEmissions,
  PurchaseOrders: CanonicalDatasetGroup.PurchaseOrders,
  // CSRD datasets - we will come back and add proper group values when we build
  // more conviction around the groupings.
  BriberyAndCorruption: CanonicalDatasetGroup.Custom,
  HumanRightsViolations: CanonicalDatasetGroup.Custom,
  Lobbying: CanonicalDatasetGroup.Custom,
  OperationalPayments: CanonicalDatasetGroup.Custom,
  SafetyIncidents: CanonicalDatasetGroup.Custom,
  WorkplaceReports: CanonicalDatasetGroup.Custom,
  WorkforceSocial: CanonicalDatasetGroup.Custom,

  Microplastics: CanonicalDatasetGroup.Custom,
};

export const CANONICAL_DATASET_GROUPING_FOR_FACILITIES_APPROVALS = {
  ...CANONICAL_DATASET_GROUPING,
  // We're removing the grouping of utilities and buildings into facilities
  Utilities: CanonicalDatasetGroup.Utilities,
  Buildings: CanonicalDatasetGroup.Buildings,
  EnergyAttributeCertificatesPurchased: CanonicalDatasetGroup.Utilities,
};

export const FINANCIALS_RELATED = [
  CanonicalDatasetKind.CapitalExpenses,
  CanonicalDatasetKind.CostOfRevenue,
  CanonicalDatasetKind.Financials,
  CanonicalDatasetKind.OperatingExpenses,
  CanonicalDatasetKind.EmployeeTravelExpenses,
] as const;

// A list of datasets allowed for plans that do not have vendor-specific
// information, so vendor mapping is not relevant
const _SUPPLIERS_EXCLUDED = [
  CanonicalDatasetKind.Buildings,
  CanonicalDatasetKind.Utilities,
  CanonicalDatasetKind.EndOfLifeTreatmentOfSoldProducts,
  CanonicalDatasetKind.UseOfSoldProductsFuels,
  CanonicalDatasetKind.CorporateInvestments,
  CanonicalDatasetKind.Employees,
  CanonicalDatasetKind.Revenue,

  // Exclude CloudCosts and CloudUsage becaues supplier mapping is implied via
  // the data ingestion schema and subsequent mapping in CTS SQL. See SUP-5800
  // Not adding CloudUsageAndCost since this dataset doesn't have vendor_entity as a field
  CanonicalDatasetKind.CloudCosts,
  CanonicalDatasetKind.CloudUsage,
] as const;

// 2024-02-15: restrict supplier mapping to financials for now as value-mapping
// address underlying reliability. The list should usually be the full set of
// datasets excluding the ones in _SUPPLIERS_EXCLUDED. See SUP-5910
const _SUPPLIERS_INCLUDED = [
  ...FINANCIALS_RELATED,
  // Supplier is required on tier 1 factory data.
  CanonicalDatasetKind.SupplierFactoriesTier1,
] as const;

export type SuppliersCanonicalDatasetKind = Exclude<
  (typeof _SUPPLIERS_INCLUDED)[number],
  (typeof _SUPPLIERS_EXCLUDED)[number]
>;

// A list of datasets that require vendor-specific information, so vendor mapping is required
const SUPPLIERS_REQUIRED_TYPED: Array<SuppliersCanonicalDatasetKind> = [
  CanonicalDatasetKind.SupplierFactoriesTier1,
] as const;

export const SUPPLIERS_REQUIRED: Array<CanonicalDatasetKind> =
  SUPPLIERS_REQUIRED_TYPED;

// A list of datasets that will be associated with a footprint but are non-emissive.
// (if adding to this list: don't forget to update NON_EMISSIVE_ACTIVITY_BATS in constants.ts too)
export const NON_EMISSIVE_DATASET_KINDS: Array<CanonicalDatasetKind> = [
  // Used to generated intensity denominators.
  CanonicalDatasetKind.Revenue,
  // Not used to generate intensity denominators.
  CanonicalDatasetKind.Sites,
  CanonicalDatasetKind.WorkplaceReports,
  CanonicalDatasetKind.HumanRightsViolations,
  CanonicalDatasetKind.BriberyAndCorruption,
  CanonicalDatasetKind.SafetyIncidents,
  CanonicalDatasetKind.OperationalPayments,
  CanonicalDatasetKind.Lobbying,
  CanonicalDatasetKind.SoldProductCircularity,
  CanonicalDatasetKind.CarbonCreditRetirements,
  CanonicalDatasetKind.GhgRemoval,
  CanonicalDatasetKind.WorkforceSocial,
];

// A list of datasets that are added to a footprint administratively and not by the user.
export const ADMIN_APPLIED_DATASET_KINDS: Array<CanonicalDatasetKind> = [
  CanonicalDatasetKind.EnergyAttributeCertificatesPurchased,
];

export function getCanonicalDatasetGroup(
  kind: CanonicalDatasetKind | null,
  isFacilitiesApprovalsEnabled: boolean
): CanonicalDatasetGroup {
  if (kind == null) {
    return CanonicalDatasetGroup.BusinessData;
  }
  if (isFacilitiesApprovalsEnabled) {
    return CANONICAL_DATASET_GROUPING_FOR_FACILITIES_APPROVALS[kind];
  }
  return CANONICAL_DATASET_GROUPING[kind];
}

export const FINANCIALS_RELATED_CANONICAL_DATASET_KINDS = [
  CanonicalDatasetKind.OperatingExpenses,
  CanonicalDatasetKind.CapitalExpenses,
  CanonicalDatasetKind.CostOfRevenue,
  CanonicalDatasetKind.Financials,
  CanonicalDatasetKind.ChartOfAccounts,
];

export function isPdfAllowedForDataset(
  canonicalDatasetKind?: CanonicalDatasetKind | null
): boolean {
  if (!canonicalDatasetKind) {
    return true;
  }

  return getKindIsFacilitiesDataset(canonicalDatasetKind);
}

// TODO: is this valid anymore?
export function isAutoBartAvailable(
  canonicalDatasetKind?: CanonicalDatasetKind | null
): boolean {
  return (
    canonicalDatasetKind === CanonicalDatasetKind.Buildings ||
    canonicalDatasetKind === CanonicalDatasetKind.Employees
  );
}

export function isFinancialsRelated(
  kind?: CanonicalDatasetKind | null
): kind is (typeof FINANCIALS_RELATED)[number] {
  if (!kind) {
    return false;
  }
  return isIncludedIn(kind, FINANCIALS_RELATED);
}

export const CANONICAL_DATASET_KIND_TO_WASTE_EMISSIONS_METHODOLOGY: Partial<
  Record<CanonicalDatasetKind, GQWasteEmissionsMethodology>
> = {
  // In preferential order -- most accurate to least accurate
  [CanonicalDatasetKind.Waste]: GQWasteEmissionsMethodology.Waste,
  [CanonicalDatasetKind.Utilities]: GQWasteEmissionsMethodology.BuildingWaste,
  [CanonicalDatasetKind.Employees]: GQWasteEmissionsMethodology.Employees,
};

/**
 * Defines a canonical mapping from CanonicalDatasetKind to a list of recommended PrimaryTypeName values.
 *
 * This helper enforces that all values are valid Array<PrimaryTypeName> entries
 * while preserving literal types for downstream type inference.
 *
 * @param map - An object mapping CanonicalDatasetKind keys to arrays of PrimaryTypeName literals
 * @returns The same map, preserving literal types
 */
function defineCanonicalMap<
  T extends { [K in CanonicalDatasetKind]: Array<PrimaryTypeName> },
>(map: T) {
  return map;
}

/**
 * Maps each CanonicalDatasetKind to a list of recommended PrimaryTypeNames
 * that are most appropriate or commonly used for that dataset.
 *
 * This is used for generating UI suggestions, validations, standardization,
 * or default selections when a dataset is selected.
 *
 * This list was generated by looking at the current statue of Postgres
 * on 2025-05-09.
 */
export const CANONICAL_DATASET_KIND_TO_RECOMMENDED_ACTIVITY_TYPES =
  defineCanonicalMap({
    [CanonicalDatasetKind.BillOfMaterials]: [
      'consumer_goods_material_assessment',
    ],
    [CanonicalDatasetKind.BriberyAndCorruption]: [
      'anti_bribery_and_corruption_incidents',
    ],
    [CanonicalDatasetKind.Buildings]: ['building_occupation'],
    [CanonicalDatasetKind.CapitalExpenses]: ['expenditure_unmapped'],
    [CanonicalDatasetKind.CarbonCreditRetirements]: [
      'carbon_credit_retirements',
    ],
    [CanonicalDatasetKind.CloudCosts]: ['cloud_spend'],
    [CanonicalDatasetKind.CloudUsage]: ['cloud_compute_usage'],
    [CanonicalDatasetKind.CloudUsageAndCost]: ['cloud_usage_and_spend'],
    [CanonicalDatasetKind.CorporateInvestments]: ['corporate_315_investments'],
    [CanonicalDatasetKind.CostOfRevenue]: ['expenditure_unmapped'],
    [CanonicalDatasetKind.CustomEmissionsFactor]: ['activity_with_custom_ef'],
    [CanonicalDatasetKind.DataCenters]: ['electricity_consumption_datacenter'],
    [CanonicalDatasetKind.DirectEmissions]: ['direct_ghg_emissions'],
    [CanonicalDatasetKind.DownstreamLogistics]: [
      'logistics',
      'expenditure_mapped',
    ],
    [CanonicalDatasetKind.EmployeeTravelExpenses]: ['expenditure_unmapped'],
    [CanonicalDatasetKind.Employees]: ['employees'],
    [CanonicalDatasetKind.EndOfLifeTreatmentOfSoldProducts]: ['waste_products'],
    [CanonicalDatasetKind.FinancedEmissions]: ['custom'],
    [CanonicalDatasetKind.Financials]: ['expenditure_unmapped'],
    [CanonicalDatasetKind.Flights]: ['travel_air'],
    [CanonicalDatasetKind.Food]: ['food_mass', 'food_spend'],
    [CanonicalDatasetKind.GhgRemoval]: ['ghg_removal'],
    [CanonicalDatasetKind.HistoricalResults]: [
      'historical_emissions',
      'historical_emissions_pv2',
    ],
    [CanonicalDatasetKind.Hotels]: ['lodging'],
    [CanonicalDatasetKind.HumanRightsViolations]: [
      'severe_human_rights_incidents',
    ],
    [CanonicalDatasetKind.Lobbying]: ['lobbying'],
    [CanonicalDatasetKind.MaterialAggregate]: [
      'custom',
      'consumer_goods_material_assessment',
      'consumer_goods_ecoinvent_material',
    ],
    [CanonicalDatasetKind.Microplastics]: ['microplastics_outflow'],
    [CanonicalDatasetKind.OperatingExpenses]: ['expenditure_unmapped'],
    [CanonicalDatasetKind.OperationalPayments]: ['payment_orders'],
    [CanonicalDatasetKind.Packaging]: [
      'consumer_goods_packaging_material',
      'physical_goods_purchase',
    ],
    [CanonicalDatasetKind.PrivateFlights]: [
      'travel_private_jets',
      'fuel_combustion',
    ],

    [CanonicalDatasetKind.PurchaseOrders]: ['purchase_orders'],
    [CanonicalDatasetKind.Rails]: ['travel_rail'],
    [CanonicalDatasetKind.Refrigerants]: ['refrigerants'],
    [CanonicalDatasetKind.Revenue]: ['intensity_denominator_monetary'],
    [CanonicalDatasetKind.SafetyIncidents]: ['health_and_safety_incidents'],
    [CanonicalDatasetKind.Sites]: ['site_land_use'],
    [CanonicalDatasetKind.SoldProductCircularity]: [
      'sold_product_circularity',
      'sold_product_recyclability',
    ],
    [CanonicalDatasetKind.SubstanceInflow]: ['substance_inflow'],
    [CanonicalDatasetKind.Substances]: ['substance_outflow'],
    [CanonicalDatasetKind.SupplierFactoriesTier1]: [
      'tier_1_factories_spend',
      'tier_1_factories_activity_data',
    ],
    [CanonicalDatasetKind.SupplierUpstreamLogistics]: [
      'expenditure_mapped',
      'logistics',
    ],

    [CanonicalDatasetKind.UpstreamLogistics]: [
      'logistics',
      'expenditure_mapped',
    ],
    [CanonicalDatasetKind.UseOfSoldProductsCommonRetail]: ['sold_retail_good'],
    [CanonicalDatasetKind.UseOfSoldProductsDataTransfer]: ['data_transfer'],
    [CanonicalDatasetKind.UseOfSoldProductsFuels]: [
      'lifetime_fuel_and_electricity_use_of_sold_products',
      'consumer_goods_textile_product_use_phase',
      'data_transfer',
      'fuel_combustion',
      'sold_refrigerant',
      'sold_vehicle',
      'sold_retail_good',
    ],
    [CanonicalDatasetKind.UseOfSoldProductsRefrigerants]: ['sold_refrigerant'],
    [CanonicalDatasetKind.UseOfSoldProductsVehicles]: ['sold_vehicle'],
    [CanonicalDatasetKind.UsePhaseOfGoods]: [
      'lifetime_fuel_and_electricity_use_of_sold_products',
    ],
    [CanonicalDatasetKind.Utilities]: [
      'electricity_consumption',
      'building_stationary_combustion',
      'electricity_consumption_datacenter',
      'refrigerants',
    ],
    [CanonicalDatasetKind.Vehicles]: [
      'vehicles',
      'non_building_electricity_consumption',
      'fuel_combustion',
    ],
    [CanonicalDatasetKind.Waste]: ['waste'],
    [CanonicalDatasetKind.WaterUsage]: ['water_usage'],
    [CanonicalDatasetKind.WorkforceSocial]: ['workers_social'],
    [CanonicalDatasetKind.WorkplaceReports]: ['workplace_reports'],
    /**
     * These canonical datasets do not appear in postgres but are in the CanonicalDatasetKind enum
     * so I kept the values that existed in the legacy code.
     */
    [CanonicalDatasetKind.CareInstructions]: [
      'consumer_goods_textile_product_use_phase',
      'consumer_goods_material_assessment',
    ],
    [CanonicalDatasetKind.ChartOfAccounts]: [],
    [CanonicalDatasetKind.CryptoHoldings]: ['crypto_holdings'],
    [CanonicalDatasetKind.CryptoTransactions]: ['crypto_transactions'],
    [CanonicalDatasetKind.EnergyAttributeCertificatesPurchased]: [],
    [CanonicalDatasetKind.Factories]: [],
    [CanonicalDatasetKind.MaterialContents]: [
      'consumer_goods_material_assessment',
    ],
    [CanonicalDatasetKind.ProductsPurchased]: [
      'consumer_goods_textile_product_use_phase',
    ],
    [CanonicalDatasetKind.ProductsSold]: [
      'waste',
      'consumer_goods_material_assessment',
      'consumer_goods_textile_product_use_phase',
    ],
    [CanonicalDatasetKind.TextileProductSales]: [],
    [CanonicalDatasetKind.UseOfGoods]: [],
    [CanonicalDatasetKind.WeightOfGoodsPurchased]: [],
  } as const);

/**
 * A type representing all PrimaryTypeNames that appear at least once
 * in CANONICAL_DATASET_KIND_TO_RECOMMENDED_ACTIVITY_TYPES.
 *
 * Useful for narrowing down to only the "recommended" activity types used across the mapping.
 */
export type AllRecommendedActivityTypes =
  (typeof CANONICAL_DATASET_KIND_TO_RECOMMENDED_ACTIVITY_TYPES)[keyof typeof CANONICAL_DATASET_KIND_TO_RECOMMENDED_ACTIVITY_TYPES][number];

export function isDatasetKind(kind: string): kind is CanonicalDatasetKind {
  return isIncludedIn(kind, Object.keys(CanonicalDatasetKind));
}
